import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { showError, showSuccess } from "../lib/toast";
import { authApi } from "../lib/authApi";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  AlertCircle,
  Sparkles,
  Shield,
  ChevronRight,
} from "lucide-react";

const resolveLocation = async (): Promise<string> => {
  try {
    const res = await fetch(
      "https://ip-api.com/json/?fields=status,city,regionName,country,query",
    );
    if (!res.ok) return;
    const data = await res.json();
    if (data.status === "success") {
      const parts = [data.city, data.regionName, data.country].filter(Boolean);
      if (parts.length) return parts.join(", ");
    }
  } catch {
    // ignore geolocation failures and fall back to backend handling
  }
  return undefined;
};

interface FormState {
  email: string;
  password: string;
  remember: boolean;
}

interface FieldError {
  email?: string;
  password?: string;
  general?: string;
}

const LoginPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>({
    email: "",
    password: "",
    remember: false,
  });
  const [errors, setErrors] = useState<FieldError>({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const validate = (): boolean => {
    const newErrors: FieldError = {};
    if (!form.email) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      newErrors.email = "Invalid email format";
    if (!form.password) newErrors.password = "Password is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const location = await resolveLocation();
      const response = await authApi.login(form.email, form.password, location);
      if (response.requires2FA) {
        localStorage.setItem("pendingLogin", JSON.stringify(response.user));
        navigate("/auth/2fa/pending", { replace: true });
        return;
      }

      const accessToken = response.accessToken;
      if (!accessToken) {
        throw new Error("Login failed. Please try again.");
      }
      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("user", JSON.stringify(response.user));

      if (!response.user.isEmailVerified) {
        showError("Please verify your email to access the platform.");
        navigate(`/verify-email?email=${encodeURIComponent(form.email)}`, {
          replace: true,
        });
        setLoading(false);
        return;
      }

      showSuccess("Login successful!");

      switch (response.user.role) {
        case "admin":
          window.location.replace("/dashboard/admin");
          break;
        case "instructor":
          window.location.replace("/dashboard/instructor");
          break;
        case "mentor":
          window.location.replace("/dashboard/mentor");
          break;
        default:
          window.location.replace("/dashboard/student");
      }
    } catch (err: unknown) {
      const error = err as Error;
      const errorMsg = error.message || "Login failed";
      if (
        errorMsg.includes("401") ||
        errorMsg.includes("Invalid") ||
        errorMsg.includes("password")
      ) {
        setErrors({ general: "Invalid email or password" });
      } else if (errorMsg.includes("403") || errorMsg.includes("verify")) {
        navigate(`/verify-email?email=${encodeURIComponent(form.email)}`, {
          replace: true,
        });
      } else {
        setErrors({ general: errorMsg });
      }
    } finally {
      setLoading(false);
    }
  };

  const setField =
    <K extends keyof FormState>(field: K) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val =
        field === "remember"
          ? (e.target as HTMLInputElement).checked
          : e.target.value;
      setForm((prev) => ({ ...prev, [field]: val }));
      if (field !== "remember")
        setErrors((prev) => ({
          ...prev,
          [field]: undefined,
          general: undefined,
        }));
    };

  return (
    <div className="flex items-center justify-center min-h-screen p-2 bg-gradient-to-br from-amber-50 via-white to-orange-50/30">
      <div className="relative w-full max-w-xs">
        {/* Main Card */}
        <div className="relative overflow-hidden border rounded-lg shadow-lg bg-white/80 backdrop-blur-sm border-white/50">
          {/* Decorative Top Bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500"></div>

          {/* Logo Section */}
          <div className="px-3 pb-1 text-center pt-7">
            <div className="inline-flex items-center justify-center w-10 h-10 mb-1">
              <div className="flex items-center gap-0">
                <span className="text-[16px] bg-orange-500 rounded-l-lg font-bold text-white transition-all pl-2 py-0.5">
                  Learn
                </span>
                <span className="text-[16px] font-bold bg-blue-500 text-white transition-all rounded-r-lg pr-2 py-0.5">
                  Hub
                </span>
              </div>
            </div>
            <h1 className="text-base font-bold text-gray-900">Welcome Back</h1>
            <p className="text-[12px] text-gray-500">
              Sign in to continue learning
            </p>
          </div>

          {/* Content */}
          <div className="px-3 pb-4">
            {/* General error */}
            {errors.general && (
              <div className="flex items-center gap-1.5 p-1.5 mb-2 border border-red-200 rounded-lg bg-red-50">
                <AlertCircle className="w-3 h-3 text-red-500 shrink-0" />
                <p className="text-[10px] font-medium text-red-600">
                  {errors.general}
                </p>
              </div>
            )}

            {/* Form Fields */}
            <div className="space-y-3">
              {/* Email */}
              <div>
                <label className="block mb-0.5 text-[11px] font-semibold text-gray-700">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute w-3 h-3 text-gray-400 -translate-y-1/2 left-2 top-1/2" />
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={setField("email")}
                    className={`w-full pl-6 pr-2 py-2.5 text-[13px] rounded-lg border bg-white/50 transition-all outline-none
                      focus:ring-1 focus:ring-orange-500/30 focus:border-orange-400 focus:bg-white
                      ${errors.email ? "border-red-300 bg-red-50" : "border-gray-200 hover:border-gray-300"}`}
                  />
                </div>
                {errors.email && (
                  <p className="mt-0.5 text-[10px] text-red-500">
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-0.5">
                  <label className="text-[11px] font-semibold text-gray-700">
                    Password
                  </label>
                  <Link
                    to="/forgot-password"
                    className="text-[11px] font-medium text-orange-500 hover:text-orange-600 transition-colors"
                  >
                    Forgot Password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute w-3 h-3 text-gray-400 -translate-y-1/2 left-2 top-1/2" />
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={form.password}
                    onChange={setField("password")}
                    className={`w-full pl-6 pr-6 py-2.5 text-[12px] rounded-lg border bg-white/50 transition-all outline-none
                      focus:ring-1 focus:ring-orange-500/30 focus:border-orange-400 focus:bg-white
                      ${errors.password ? "border-red-300 bg-red-50" : "border-gray-200 hover:border-gray-300"}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((p) => !p)}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-3 h-3" />
                    ) : (
                      <Eye className="w-3 h-3" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-0.5 text-[10px] text-red-500">
                    {errors.password}
                  </p>
                )}
              </div>

              {/* Remember Me */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.remember}
                    onChange={setField("remember")}
                    className="w-3 h-3 text-orange-500 border-gray-300 rounded focus:ring-orange-500 focus:ring-offset-0"
                  />
                  <span className="text-[12px] text-gray-600">Remember me</span>
                </label>
                <div className="flex items-center gap-1 text-[9px] text-gray-400">
                  <Shield className="w-2.5 h-2.5" />
                  <span>Secure</span>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full flex items-center justify-center gap-1.5 py-2.5 mt-3 text-[13px] font-semibold text-white rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md transition-all disabled:opacity-50 group"
            >
              {loading ? (
                <>
                  <svg
                    className="w-3 h-3 text-white animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8z"
                    />
                  </svg>
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                  <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </button>

            {/* Divider */}
            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-[10px]">
                <span className="px-2 text-gray-400 bg-white/80">
                  or continue with
                </span>
              </div>
            </div>

            {/* Google Login Button */}
            {authApi.getGoogleClientId && authApi.getGoogleClientId() && (
              <button
                type="button"
                onClick={() => authApi.loginWithGoogle()}
                className="flex items-center justify-center w-full gap-1.5 py-2.5 text-[13px] font-medium text-gray-700 transition-all duration-200 bg-white border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>
            )}

            {/* Sign Up Link */}
            <div className="mt-3 text-center">
              <p className="text-[12px] text-gray-500">
                Don't have an account?{" "}
                <Link
                  to="/register"
                  className="font-semibold text-orange-500 hover:text-orange-600 transition-colors inline-flex items-center gap-0.5"
                >
                  Create account
                  <ChevronRight className="w-2.5 h-2.5" />
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Security Footer */}
        <div className="mt-2 text-center">
          <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/60 backdrop-blur-sm rounded-full">
            <Shield className="w-2 h-2 text-emerald-500" />
            <span className="text-[10px] text-gray-500">2FA Available</span>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in { animation: fade-in 0.25s ease-out; }
      `}</style>
    </div>
  );
};

export default LoginPage;
